/*
 * Carbisdale - OutlineTree
 *
 * Pure Functions für die Berechnung des Outline-Baums.
 * Bestimmt die hierarchische Struktur aus dem Paragraph-Typ und outlineDepth.
 *
 * v4-Modell (Outline-Redesign Phase 5):
 *   - Nur Outline-Items (type='outline-item') tragen eine sinnvolle
 *     outlineDepth und bilden die Hierarchie.
 *   - Normal-Paragraphen (type='normal') haben keine eigene Tiefe; sie gehören
 *     in den Zuordnungsbereich des unmittelbar vorhergehenden Outline-Items
 *     innerhalb derselben Section.
 *   - Range eines Outline-Items O endet beim ersten Outline-Item I mit
 *     I.outlineDepth ≤ O.outlineDepth, einem Separator oder Dokumentenende.
 */

/**
 * Outline-Node: Repräsentiert einen Absatz im Gliederungsbaum.
 */
export class OutlineNode {
    /**
     * @param {number} paragraphIndex - Index des Absatzes im Model
     * @param {number} depth - Tiefe; bei Normal-Paras später auf Owner-Tiefe gesetzt
     * @param {boolean} isActive - true gdw. type === 'outline-item'
     */
    constructor(paragraphIndex, depth, isActive) {
        this.paragraphIndex = paragraphIndex
        this.depth = depth
        this.isActive = isActive
        this.parentIndex = -1       // Index des Parent-OutlineNode (-1 = Root)
        this.isLastChild = false    // Für └ vs │
        this.childIndices = []      // Indizes der Kinder-OutlineNodes
    }
}

/**
 * Baut den Outline-Baum aus dem Paragraph-Array.
 *
 * Stack-basierte Ordnungsregel (v4):
 *   - Outline-Item O mit Tiefe N: Pop alle Stack-Einträge mit Tiefe ≥ N.
 *     Parent = neuer Stack-Top. Push N.
 *   - Normal-Para: gehört zum aktuellen Stack-Top als Kind. Manipuliert den
 *     Stack nicht; übernimmt die Owner-Tiefe für korrekte Connector-Spalten.
 *   - Separator: Stack komplett zurücksetzen (neuer unabhängiger Baum).
 *
 * @param {Paragraph[]} paragraphs - Array der Absätze
 * @returns {OutlineNode[]} Array von OutlineNodes (1:1 Mapping zu paragraphs)
 */
export function buildOutlineTree(paragraphs) {
    if (!paragraphs || paragraphs.length === 0) return []

    const nodes = paragraphs.map((p, i) =>
        new OutlineNode(i, p.outlineDepth, p.isOutlineItem())
    )

    // Stack: [{depth, nodeIndex}] — virtueller Root
    const stack = [{ depth: -1, nodeIndex: -1 }]

    for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i]

        if (paragraphs[i].type === 'separator') {
            node.isSeparator = true
            while (stack.length > 1) stack.pop()
            continue
        }

        if (node.isActive) {
            // Outline-Item: gleiche oder höhere Tiefe schließt darüberliegende Items.
            while (stack.length > 1 && node.depth <= stack[stack.length - 1].depth) {
                stack.pop()
            }
            const parent = stack[stack.length - 1]
            node.parentIndex = parent.nodeIndex
            if (parent.nodeIndex >= 0) {
                nodes[parent.nodeIndex].childIndices.push(i)
            }
            stack.push({ depth: node.depth, nodeIndex: i })
        } else {
            // Normal-Para: gehört zum aktuellen Stack-Top, kein Push, kein Pop.
            const parent = stack[stack.length - 1]
            node.parentIndex = parent.nodeIndex
            if (parent.nodeIndex >= 0) {
                nodes[parent.nodeIndex].childIndices.push(i)
                // Owner-Tiefe übernehmen, damit Connectors auf der richtigen Spalte landen.
                node.depth = parent.depth
            } else {
                // Freistehender Normal-Para vor jedem Outline-Item: keine Tiefe.
                node.depth = 0
            }
        }
    }

    _computeIsLastChild(nodes)

    return nodes
}

/**
 * Berechnet isLastChild für alle Nodes.
 * Ein Node ist isLastChild, wenn kein Geschwister-Node (mit gleichem Parent) nach ihm kommt.
 *
 * @param {OutlineNode[]} nodes
 */
function _computeIsLastChild(nodes) {
    // Gruppiere nach parentIndex
    const childrenByParent = new Map()

    for (let i = 0; i < nodes.length; i++) {
        const parentIdx = nodes[i].parentIndex
        if (!childrenByParent.has(parentIdx)) {
            childrenByParent.set(parentIdx, [])
        }
        childrenByParent.get(parentIdx).push(i)
    }

    // Das letzte Kind jedes Parents markieren
    for (const children of childrenByParent.values()) {
        if (children.length > 0) {
            nodes[children[children.length - 1]].isLastChild = true
        }
    }
}

/**
 * Berechnet die Outline-Spalten für jede Screen-Zeile.
 * Für die zeilen-synchrone Darstellung im Outline-Pane.
 *
 * Jede Tiefe d belegt 2 Spalten: d*2, d*2+1
 * - Spalte d*2+1 ist die "Connector"-Position (│, └ oder -)
 * - (-) erstreckt sich über d*2, d*2+1, d*2+2 — die ) ragt in die
 *   erste Spalte der nächsten Tiefe (dort immer frei, da zwei Marker
 *   nie auf derselben Zeile stehen können)
 *
 * Klammer-Prinzip: Jede vertikale Linie beginnt mit (-) und endet mit └
 * auf der letzten Screen-Zeile des Zuordnungsbereichs.
 *
 * @param {Paragraph[]} paragraphs - Absätze aus dem Model
 * @param {ScreenLine[]} screenLines - Screen-Zeilen aus dem EditorView
 * @returns {Map<number, Array<{col: number, char: string, style: string, highlightStyle?: string}>>}
 *          lineIndex → Spalten-Array. style = Normal-Style, highlightStyle = Style wenn Absatz aktiv (Cursor)
 */
export function buildOutlineColumns(paragraphs, screenLines) {
    if (!paragraphs || !screenLines || screenLines.length === 0) {
        return new Map()
    }

    const nodes = buildOutlineTree(paragraphs)
    if (nodes.length === 0) return new Map()

    // Maximale Tiefe ermitteln
    let maxDepth = 0
    for (const node of nodes) {
        if (node.depth > maxDepth) maxDepth = node.depth
    }

    // Index-Map: paragraphId → paragraphIndex
    const paraIdToIndex = new Map()
    for (let i = 0; i < paragraphs.length; i++) {
        paraIdToIndex.set(paragraphs[i].id, i)
    }

    const result = new Map()

    // Pro Tiefe: Referenz auf die letzte │ merken für └-Erkennung
    const lastVerticalAt = new Array(maxDepth + 1).fill(null)

    // Abschnitts-Einrückung: 2 * Separator-Tiefe für alle Outline-Zeilen im Abschnitt
    let sectionIndent = 0

    for (let lineIdx = 0; lineIdx < screenLines.length; lineIdx++) {
        const screenLine = screenLines[lineIdx]
        const paraIndex = paraIdToIndex.get(screenLine.paragraphId)
        if (paraIndex === undefined) continue

        const node = nodes[paraIndex]

        // Separator: Alle offenen Segmente schließen, Abschnitts-Indent aktualisieren
        if (node.isSeparator) {
            for (let d = 0; d <= maxDepth; d++) {
                if (lastVerticalAt[d]) {
                    lastVerticalAt[d].char = '└'
                    lastVerticalAt[d] = null
                }
            }
            sectionIndent = paragraphs[paraIndex].outlineDepth * 2
            continue
        }

        const columns = []

        if (screenLine.isFirstLine) {
            _buildFirstLineColumns(node, nodes, paraIndex, maxDepth, columns, paragraphs[paraIndex])
        } else {
            _buildContinuationColumns(node, nodes, paraIndex, maxDepth, columns)
        }

        // Abschnitts-Indent auf alle Spalten-Positionen addieren
        if (sectionIndent > 0) {
            for (const col of columns) {
                col.col += sectionIndent
            }
        }

        if (columns.length > 0) {
            result.set(lineIdx, columns)
        }

        // └-Erkennung: Pro Tiefe prüfen ob Segment weiterläuft
        for (let d = 0; d <= maxDepth; d++) {
            const colPos = d * 2 + 1 + sectionIndent
            let found = null
            for (let i = 0; i < columns.length; i++) {
                if (columns[i].col === colPos && columns[i].char === '│') {
                    found = columns[i]
                    break
                }
            }

            if (found) {
                lastVerticalAt[d] = found
            } else if (lastVerticalAt[d]) {
                // Segment endet: vorherige │ wird └
                lastVerticalAt[d].char = '└'
                lastVerticalAt[d] = null
            }
        }
    }

    // Noch offene Segmente am Dokumentende schließen
    for (let d = 0; d <= maxDepth; d++) {
        if (lastVerticalAt[d]) {
            lastVerticalAt[d].char = '└'
        }
    }

    return result
}

/**
 * Erzeugt die Spalten für die erste Zeile eines Absatzes.
 * Styles werden als Dual-Style gespeichert: style = Standard, highlightStyle = wenn Absatz aktuell (Cursor).
 */
function _buildFirstLineColumns(node, nodes, paraIndex, maxDepth, columns, para) {
    const depth = node.depth

    for (let d = 0; d <= maxDepth; d++) {
        const colBase = d * 2

        if (d === depth) {
            if (node.isActive) {
                // Aktiver Node: (-) Marker — bzw. (+) wenn activeCollapsed.
                // Einheitliches Schema (wie die Linien): passiv Connector-Farbe,
                // aktiv (Cursor auf dem Absatz) Active-Border-Color. Im Proportional
                // Mode überschreibt _outlineIconStyles() das ohnehin (+ Hover).
                const markerChar = (para && para.activeCollapsed) ? '+' : '-'
                columns.push({ col: colBase, char: '(', style: 'OUTLINE_CONNECTOR', highlightStyle: 'OUTLINE_CURRENT' })
                columns.push({ col: colBase + 1, char: markerChar, style: 'OUTLINE_CONNECTOR', highlightStyle: 'OUTLINE_CURRENT' })
                columns.push({ col: colBase + 2, char: ')', style: 'OUTLINE_CONNECTOR', highlightStyle: 'OUTLINE_CURRENT' })
            } else {
                // Passiver Node: vertikale Linie (└ wird im Post-Processing gesetzt)
                columns.push({ col: colBase + 1, char: '│', style: 'OUTLINE_CONNECTOR', highlightStyle: 'OUTLINE_CURRENT' })
            }
        } else if (d < depth) {
            // Ancestor-Level: Linie wenn Absatz im Segment liegt (kein Highlight)
            if (_isInSegmentAt(d, paraIndex, nodes)) {
                columns.push({ col: colBase + 1, char: '│', style: 'OUTLINE_CONNECTOR' })
            }
        }
    }
}

/**
 * Erzeugt die Spalten für Folge-Zeilen eines Absatzes (Zeile 2+).
 */
function _buildContinuationColumns(node, nodes, paraIndex, maxDepth, columns) {
    const depth = node.depth

    for (let d = 0; d <= maxDepth; d++) {
        const colBase = d * 2

        if (d === depth) {
            // Eigene Tiefe: vertikale Linie, hervorgehoben beim aktuellen Absatz
            columns.push({ col: colBase + 1, char: '│', style: 'OUTLINE_CONNECTOR', highlightStyle: 'OUTLINE_CURRENT' })
        } else if (d < depth) {
            if (_isInSegmentAt(d, paraIndex, nodes)) {
                columns.push({ col: colBase + 1, char: '│', style: 'OUTLINE_CONNECTOR' })
            }
        }
    }
}

/**
 * Prüft ob ein Absatz im Segment einer vertikalen Linie bei Tiefe d liegt.
 * Gibt true für ALLE Absätze im Segment (inkl. letztem).
 *
 * @param {number} d - Tiefe der vertikalen Linie
 * @param {number} paraIndex - Aktueller Absatz-Index
 * @param {OutlineNode[]} nodes - Alle Nodes
 * @returns {boolean}
 */
function _isInSegmentAt(d, paraIndex, nodes) {
    // Tiefe 0: Alle Absätze gehören zum Root-Segment
    if (d === 0) return true

    // Prüfe ob ein aktiver Ancestor bei Tiefe d existiert
    let ancestorIndex = nodes[paraIndex].parentIndex
    while (ancestorIndex >= 0) {
        if (nodes[ancestorIndex].depth === d && nodes[ancestorIndex].isActive) {
            return true
        }
        ancestorIndex = nodes[ancestorIndex].parentIndex
    }

    return false
}
